import { apiClient } from './client';

// ─── Meta ─────────────────────────────────────────────────────────────────────

export interface TourismOption {
  value: string;
  count: number;
}

export interface TourismGruppeOption {
  id: number;
  name: string;
  listings: number;
}

export type TourismDimension =
  | 'neighborhood'
  | 'listing_type'
  | 'offer_provider'
  | 'price'
  | 'size'
  | 'guests'
  | 'bedrooms'
  | 'rating'
  | 'zipcode'
  | 'gruppe';

export interface TourismMeta {
  /** Frühester Snapshot-Tag dieser Instanz – begrenzt die Zeitraumwahl nach unten. */
  data_start: string | null;
  horizon_end: string | null;
  last_scrape_at: string | null;
  listings_total: number;
  scrape_days: { day: string; listings: number }[];
  options: {
    listing_types: TourismOption[];
    neighborhoods: TourismOption[];
    offer_providers: TourismOption[];
    zipcodes: TourismOption[];
    gruppen: TourismGruppeOption[];
    ranges: {
      price: [number | null, number | null];
      size_sqm: [number | null, number | null];
      guests: [number | null, number | null];
      bedrooms: [number | null, number | null];
      rating: [number | null, number | null];
    };
  };
  /** Anteil der Inserate, für die das Merkmal bekannt ist (0–1). */
  coverage: Record<string, number>;
  dimensions: TourismDimension[];
}

// ─── Overview ─────────────────────────────────────────────────────────────────

export interface TourismStats {
  occupancy: number | null;
  unavailable_days: number;
  known_days: number;
  booking_signals: number;
  listings_with_data: number;
}

export interface TourismKpis extends TourismStats {
  listings_matched: number;
}

/** Kennzahlen eines einzelnen Tages. */
export interface TourismDayStats {
  unavailable: number;
  /**
   * Zahl der Inserate mit Angabe an diesem Tag. Heißt `known`, weil es zugleich
   * die bekannten Tage sind: pro Inserat und Tag gibt es höchstens eine Zeile,
   * beide Größen fallen also zusammen.
   */
  known: number;
  signals: number;
}

export interface TourismDay {
  /** ISO-Tag, z. B. "2026-08-20". */
  date: string;
  selection: TourismDayStats;
  reference: TourismDayStats;
}

export interface TourismBucket extends TourismStats {
  key: string;
  listings: number;
}

export interface TourismOverview {
  date_from: string;
  date_to: string;
  today: string;
  dimension: TourismDimension;
  filters_active: boolean;
  /** Ein Punkt je Tag, aufsteigend. Tage ohne jede Angabe fehlen hier. */
  series: TourismDay[];
  selection_kpis: TourismKpis;
  reference_kpis: TourismKpis;
  breakdown: TourismBucket[];
  /** Inserate, für die das Breakdown-Merkmal unbekannt ist – kein Marktsegment. */
  unknown: TourismBucket | null;
}

export interface TourismOverviewParams {
  date_from?: string;
  date_to?: string;
  dimension?: TourismDimension;
  listing_types?: string[];
  neighborhoods?: string[];
  offer_providers?: string[];
  zipcodes?: string[];
  gruppe_ids?: number[];
  price_min?: number;
  price_max?: number;
  size_min?: number;
  size_max?: number;
  guests_min?: number;
  guests_max?: number;
  bedrooms_min?: number;
  bedrooms_max?: number;
  rating_min?: number;
  rating_max?: number;
}

export const tourismApi = {
  getMeta: async (): Promise<TourismMeta> => {
    const { data } = await apiClient.get('/tourism/meta');
    return data;
  },
  getOverview: async (params: TourismOverviewParams): Promise<TourismOverview> => {
    const { data } = await apiClient.get('/tourism/overview', { params });
    return data;
  },
};
